/**
 * CALENDAR & IDEAS — the cabinet's month view.
 *
 * It answers one question — *what is the sky doing, what have I got out, and is that slot already
 * crowded?* — and it makes two reads to do it, besides the catalogue's for the category names
 * (`features/calendar/category-labels.ts`):
 *   getAuthorCalendar   the events, this author's own pieces, and the per-day load
 *   getPaymentsView     once per visit, for the platform's own now (THE MONTH, 2) and today.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ★★ THE MONTH, AND WHY THIS SCREEN NEVER ASKS THE DEVICE WHAT DAY IT IS
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * `getAuthorCalendar` REQUIRES BOTH BOUNDS (`authors-api.yaml`) — unlike `getDashboard`,
 * which this app calls with no range at all precisely so the SERVER picks it
 * (`routes/dashboard/DashboardRoute.tsx`). And this app has no clock and must not have one:
 * `features/cabinet/format.ts` says so in terms, C11 bans the globals across `web/`
 * (`eslint.config.mjs`, one exempt path and it is in `src/`), and the ONE inline disable in
 * the whole repo is the ADMIN console's single browser tick
 * (`web/admin/src/features/queues/model/clock.ts`). A second one here would be the second.
 *
 * So the month comes from one of two places, in this order, and from nowhere else:
 *
 *   1. THE ADDRESS — `/calendar?month=2026-09`. An explicit choice, which is the other half of the
 *      rule `format.ts` already writes: "ranges come from the RESPONSE … or from an explicit
 *      choice, never from the page". It survives a reload and can be sent to somebody.
 *   2. THE PLATFORM'S OWN MONTH - read off `getPaymentsView`'s `computedAt` (`./platform-month.ts`).
 *      It opens on the current month with its day framed (the owner, 2 November 2026); it used to
 *      open on the month of the next sky event. Only when that read fails does the screen ask, with
 *      the chooser and the reason. The device's clock is never read.
 *
 * That read is made once per visit and serves twice more: "THIS MONTH" (design 15) goes to its
 * month with no second read, a failure said and never guessed around; the grid marks its DAY.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ★ THE THREE PANES THAT ARE NOT DRAWN
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * The theme chips, "13 pieces already in motion · 4 themes open", and headline starters. All three
 * are PLATFORM WORK OWED — `docs/ui/04-pages.md` §3.6 proves the absence in python against
 * both specs (`saturation 0, theme 0, pitch 0, busy 0`), and `:647` §8 names calendar theme
 * saturation as one of the three biggest gaps still needing an owner's decision. None is drawn and
 * none is faked; the owner's design-15 decision 1 ("leave out what nothing backs") keeps it that
 * way, and `features/calendar/components/NotDrawn.tsx` is now the one plain line an author reading
 * the counts is owed - other authors are counted, never named.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ★ DESIGN 15 - THE PROTOTYPE'S LOOK (the owner's live look at real cabinets, 2026-10-01)
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * `moonx-authors-prototype/MoonX App.dc.html`, `cabIsCalendar`: the header and its second quiet
 * line, the ‹ month › row with "This month" ruled off under it, the legend of category dots with the
 * month's summary at its right ("12 articles across MoonX · 2 yours"), then the month - day cards in
 * a grid - beside the chosen day's pane. Kept: the month model, its reads, the opening month, the
 * chooser, the day pane and every state. Gone from what an author reads: "UTC", "the platform's own
 * clock", "This app keeps no clock of its own" and the footer that printed the response's range
 * "(exclusive), on the platform's own clock" - the month's name says the same thing in words.
 *
 * ROUTE. `/calendar`, mounted by its `CABINET_ROUTES` entry in `features/cabinet/routes.ts`, which
 * imports this component directly; the package-local descriptor it was copied from is gone. Layout,
 * wording and navigation are ours (rows 99/100/108); the corpus fixes only `/enter`, not this one.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Stack, Text } from '@mantine/core';
import {
  CabinetPageHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  PartialFailureNote,
  RefreshingNote,
} from '../../features/cabinet/components';
import { useAsync } from '../../features/cabinet/state';
import { useCalendarApi } from '../../features/calendar/api';
import { useCategoryLabels } from '../../features/calendar/category-labels';
import { useGroupNames } from '../../features/calendar/group-names';
import {
  CalendarTodayProvider,
  useCalendarToday,
  CategoryLegend,
  DayDetail,
  MonthChooser,
  MonthGrid,
  NotDrawn,
  bucketByDay,
  categoriesIn,
  categoryMarks,
} from '../../features/calendar/components';
import {
  daysOfMonth,
  isMonthKey,
  monthLabel,
  monthWindow,
} from '../../features/calendar/domain/month';
import type { MonthKey } from '../../features/calendar/domain/month';
import { usePlatformMonth } from './platform-month';
import { useTranslation } from '../../i18n';
import type { TFunction } from 'i18next';
import classes from './calendar-route.module.css';

export interface CalendarRouteProps {
  /**
   * The month to show, if the shell knows it. OPTIONAL, and every prop on this component is, because
   * the cabinet's router mounts `CABINET_ROUTES[].Component` with NO props at all
   * (`web/authors/src/router.tsx`, the `cabinetRoutes` map) — a required prop would be a compile
   * error in a file this package does not own, and `CabinetRoute.Component` is typed
   * `FunctionComponent`, which is `FunctionComponent<{}>`.
   */
  readonly month?: MonthKey;
  /** Where a month change goes, if the shell owns the address. */
  readonly onMonthChange?: (month: MonthKey) => void;
}

/**
 * Read the month out of the address.
 *
 * `window.location.search` rather than a router hook, for the same reason
 * `routes/apply/index.tsx` reads it that way: this screen has to render when it is mounted with
 * no props, no provider and — in a preview or a unit test — no `RouterProvider` above it, and
 * TanStack's `useRouter` throws outside one. When the shell grows a navigation seam it passes
 * `month` and `onMonthChange` and this function stops being reached.
 */
function monthFromAddress(): MonthKey | undefined {
  const raw = new URLSearchParams(window.location.search).get('month');
  return isMonthKey(raw) ? raw : undefined;
}

/** Write it back, so the address is shareable. `replaceState`, never `pushState`: paging through
 *  twelve months must not fill the back button — the rule `features/queues/model/navigation.tsx`
 *  states for every filter in the other SPA. */
function writeMonthToAddress(month: MonthKey): void {
  const params = new URLSearchParams(window.location.search);
  params.set('month', month);
  window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
}

export function CalendarRoute({ month, onMonthChange }: CalendarRouteProps) {
  const { t } = useTranslation('calendar');

  // The address is read ONCE, at mount. Re-reading it on every render would fight the writes below.
  const [ownMonth, setOwnMonth] = useState<MonthKey | undefined>(() => month ?? monthFromAddress());
  const active = month ?? ownMonth;

  /** "This month": pressed and not yet answered; and whether its answer could not be read. */
  const [thisMonthAsked, setThisMonthAsked] = useState(false);
  const [thisMonthFailed, setThisMonthFailed] = useState(false);
  /** Bumped by "This month", so the frame goes back to today on a month already on screen too. */
  const [backToToday, setBackToToday] = useState(0);

  const showMonth = useCallback(
    (next: MonthKey) => {
      if (onMonthChange === undefined) {
        setOwnMonth(next);
        writeMonthToAddress(next);
        return;
      }
      onMonthChange(next);
    },
    [onMonthChange],
  );

  /** The reader's own move. */
  const changeMonth = useCallback(
    (next: MonthKey) => {
      setThisMonthAsked(false);
      setThisMonthFailed(false);
      showMonth(next);
    },
    [showMonth],
  );

  // The platform's own now, read once per visit (./platform-month.ts): its month when there is none
  // in the address or when the reader asks for "This month", and its day for the grid's today.
  const platform = usePlatformMonth(active, 'ready', thisMonthAsked);

  useEffect(() => {
    if (active === undefined && platform.month !== undefined) {
      showMonth(platform.month);
    }
  }, [active, platform.month, showMonth]);

  // "This month", once its answer is in: go there, or say it could not be read.
  useEffect(() => {
    if (!thisMonthAsked || platform.pending) {
      return;
    }
    if (platform.month === undefined) {
      setThisMonthAsked(false);
      setThisMonthFailed(true);
      return;
    }
    setBackToToday((count) => count + 1);
    changeMonth(platform.month);
  }, [thisMonthAsked, platform.pending, platform.month, changeMonth]);

  return (
    <Stack gap="lg">
      <Stack gap={6}>
        <CabinetPageHeader title={t('route.title')} lede={t('route.lede')} />
        <NotDrawn />
      </Stack>

      {active === undefined ? (
        <ChoosingAMonth loading={platform.pending} onChoose={changeMonth} />
      ) : (
        <CalendarTodayProvider value={platform.today}>
          <div className={classes.navRow}>
            <MonthChooser month={active} onChange={changeMonth}>
              <Button
                variant="default"
                radius="xl"
                size="xs"
                loading={thisMonthAsked && platform.pending}
                onClick={() => {
                  setThisMonthFailed(false);
                  setThisMonthAsked(true);
                }}
              >
                {t('route.thisMonth')}
              </Button>
            </MonthChooser>
          </div>
          {thisMonthFailed ? (
            <Text size="sm" c="dimmed" role="status">
              {t('route.currentMonthFailed')}
            </Text>
          ) : null}
          <CalendarMonth month={active} backToToday={backToToday} />
        </CalendarTodayProvider>
      )}
    </Stack>
  );
}

/**
 * The state before a month is known.
 *
 * It is a REAL SCREEN and not a spinner with a shrug: it says why the calendar cannot open on a
 * month by itself, and it offers the control that settles it. Nothing here guesses.
 */
function ChoosingAMonth({
  loading,
  onChoose,
}: {
  loading: boolean;
  onChoose: (month: MonthKey) => void;
}) {
  const { t } = useTranslation('calendar');
  if (loading) {
    return <LoadingState label={t('route.openingMonth')} />;
  }

  return (
    <Stack gap="md">
      <EmptyState
        headline={t('route.whichMonth.headline')}
        explanation={t('route.whichMonth.explanation')}
      />

      {/* Rendered with a month already in it so the control is usable, and that month is NOT a
          guess at today: it is the first month of the year the browser's own picker will open on
          when the reader touches it. The value is only ever sent once they choose. */}
      <MonthChooser month="2026-01" onChange={onChoose} />
    </Stack>
  );
}

function CalendarMonth({ month, backToToday }: { month: MonthKey; backToToday: number }) {
  const { t } = useTranslation('calendar');
  const api = useCalendarApi();
  const labels = useCategoryLabels();
  const groupNames = useGroupNames();
  const range = useMemo(() => monthWindow(month), [month]);

  const read = useCallback(
    (signal: AbortSignal) => api.getAuthorCalendar(range, signal),
    [api, range],
  );
  const calendar = useAsync(read, `calendar:${month}`);

  // The day the pane shows. Reset whenever the month changes — a selected day from August has no
  // cell in September, and a pane describing a day that is not on screen is worse than none.
  const today = useCalendarToday();
  const [selectedDay, setSelectedDay] = useState<string | undefined>(undefined);
  useEffect(() => {
    setSelectedDay(undefined);
  }, [month, backToToday]);

  const buckets = useMemo(
    () =>
      bucketByDay(
        month,
        calendar.data?.events ?? [],
        calendar.data?.articles ?? [],
        calendar.data?.dayLoad ?? [],
      ),
    [month, calendar.data],
  );
  // One mark per category the month has, shared by the legend, the days and the pane.
  const marks = useMemo(
    () => categoryMarks(categoriesIn(calendar.data?.dayLoad ?? []), labels),
    [calendar.data, labels],
  );

  // Nothing chosen: today when this is its month, else the month's first day.
  const days = daysOfMonth(month);
  const shown = selectedDay ?? (today !== undefined && days.includes(today) ? today : days[0]) ?? '';
  const bucket = buckets.find((candidate) => candidate.day === shown) ?? buckets[0];

  if (calendar.status === 'error' && calendar.data === undefined) {
    return (
      <ErrorState
        title={t('route.monthFailed', { month: monthLabel(month) })}
        error={calendar.error}
        onRetry={calendar.refetch}
      />
    );
  }

  if (calendar.data === undefined || bucket === undefined) {
    return <LoadingState label={t('route.readingMonth', { month: monthLabel(month) })} rows={3} />;
  }

  // The month's own counts, as the response gives them - never a figure this screen made up.
  const across = calendar.data.dayLoad.reduce((sum, row) => sum + row.scheduled, 0);
  const yours = calendar.data.articles.length;

  return (
    <Stack gap="md">
      {calendar.status === 'error' ? (
        <PartialFailureNote what={t('route.lastRefresh')} error={calendar.error} />
      ) : null}

      <div className={classes.legendRow}>
        <CategoryLegend marks={marks} />
        <span className={classes.summary}>{monthSummary(across, yours, t)}</span>
      </div>

      <div className={classes.layout}>
        <MonthGrid
          month={month}
          buckets={buckets}
          selectedDay={shown}
          onSelectDay={setSelectedDay}
          marks={marks}
        />
        <DayDetail bucket={bucket} labels={labels} marks={marks} groupNames={groupNames} />
      </div>

      {calendar.isRefreshing ? <RefreshingNote label={t('route.refreshing')} /> : null}
    </Stack>
  );
}

/** The prototype's "9 pieces · 2 yours", for the month: the count across MoonX includes the author. */
function monthSummary(across: number, yours: number, t: TFunction<'calendar'>): string {
  if (across === 0 && yours === 0) {
    return t('route.summary.nothing');
  }
  return t('route.summary.across', { count: across, yours });
}
